import { Component, OnInit, AfterViewInit, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/_services/auth.service';
import { appConfig } from 'src/app/_core/config/app-config.const';
import { AdminAnalyticsService, AnalyticsPeriod } from '../../services/admin-analytics.service';
import {
  contentTypeStatus, contentTypeTier,
  ratingTier,
  featureStatus, featureTier,
  UNDERUSED_FEATURE_THRESHOLD,
  StatusTier,
} from './super-admin-analytics-dashboard.config';

export interface FeatureRow {
  feature: string;
  uses: number;
  uniqueUsers: number;
  utilizationPct: number;
}

export interface ContentTypeRow {
  type: string;
  count: number;
  avgCompletion: number;
  avgRating: number;
}

export interface PlatformMetrics {
  totalUsers: number;
  activeUsersPct: number;
  totalTangrams: number;
  tangramsViewed: number;
}

export interface ContentIntelligence {
  avgCompletionRate: number;
  avgRating: number;
}

@Component({
  selector: 'tangram-super-admin-analytics-dashboard',
  templateUrl: './super-admin-analytics-dashboard.component.html',
  styleUrls: ['./super-admin-analytics-dashboard.component.scss'],
})
export class SuperAdminAnalyticsDashboardComponent implements OnInit, AfterViewInit {
  imgPath = appConfig.imagePath;
  currentUser: any;

  // Per-section loading flags so each block can render independently.
  loadingKpis = false;
  loadingContentTypes = false;
  loadingFeatures = false;

  selectedPeriod = 'This Month';
  readonly periodOptions = ['This Week', 'This Month', 'This Term', 'All Time'];

  private readonly periodMap: Record<string, AnalyticsPeriod> = {
    'This Week':  'week',
    'This Month': 'month',
    'This Term':  'term',
    'All Time':   'all',
  };

  private get period(): AnalyticsPeriod {
    return this.periodMap[this.selectedPeriod] || 'all';
  }

  onPeriodChange(): void {
    this.loadKpis();
    this.loadContentTypes();
    this.loadFeatures();
  }

  platformMetrics: PlatformMetrics = {
    totalUsers: 0,
    activeUsersPct: 0,
    totalTangrams: 0,
    tangramsViewed: 0,
  };

  contentIntelligence: ContentIntelligence = { avgCompletionRate: 0, avgRating: 0 };

  featureRows: FeatureRow[] = [];
  contentTypeRows: ContentTypeRow[] = [];

  get loading(): boolean {
    return this.loadingKpis || this.loadingContentTypes || this.loadingFeatures;
  }

  get underusedFeatures(): FeatureRow[] {
    return this.featureRows.filter(r => r.utilizationPct < UNDERUSED_FEATURE_THRESHOLD);
  }

  // ── Status helpers (delegated to config) ──────────────────────────────────
  contentTypeStatus(avgCompletion: number) { return contentTypeStatus(avgCompletion); }
  contentTypeTier(avgCompletion: number): StatusTier { return contentTypeTier(avgCompletion); }
  ratingTier(avgRating: number): StatusTier { return ratingTier(avgRating); }
  featureStatus(utilizationPct: number) { return featureStatus(utilizationPct); }
  featureTier(utilizationPct: number): StatusTier { return featureTier(utilizationPct); }

  typeChipClass(type: string): string {
    const map: Record<string, string> = {
      youtube:  'type-chip--youtube',
      spotify:  'type-chip--spotify',
      pdf:      'type-chip--pdf',
      article:  'type-chip--article',
      vimeo:    'type-chip--vimeo',
    };
    return map[(type || '').toLowerCase()] || 'type-chip--other';
  }

  // ── Pagination ────────────────────────────────────────────────────────────
  readonly PAGE_SIZE = 5;
  contentTypePage = 1;
  featurePage     = 1;

  paginate<T>(items: T[], page: number): T[] {
    return items.slice((page - 1) * this.PAGE_SIZE, page * this.PAGE_SIZE);
  }
  totalPages(count: number): number { return Math.max(1, Math.ceil(count / this.PAGE_SIZE)); }
  pageRange(count: number): number[] { return Array.from({ length: this.totalPages(count) }, (_, i) => i + 1); }
  pageEnd(page: number, total: number): number { return Math.min(page * this.PAGE_SIZE, total); }

  get pagedContentTypeRows(): ContentTypeRow[] { return this.paginate(this.contentTypeRows, this.contentTypePage); }
  get pagedFeatureRows(): FeatureRow[]         { return this.paginate(this.featureRows,     this.featurePage); }

  exportCsv(): void {
    const escape = (v: unknown): string => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const row = (cells: unknown[]) => cells.map(escape).join(',');

    const sections: string[] = [];

    sections.push(`Admin Analytics Export`);
    sections.push(`Period,${escape(this.selectedPeriod)}`);
    sections.push(`Generated,${escape(new Date().toISOString())}`);
    sections.push('');

    sections.push('Platform KPIs');
    sections.push(row(['Metric', 'Value']));
    sections.push(row(['Total Users',          this.platformMetrics.totalUsers]));
    sections.push(row(['Active Users (%)',     `${this.platformMetrics.activeUsersPct}%`]));
    sections.push(row(['Total Tangrams',       this.platformMetrics.totalTangrams]));
    sections.push(row(['Tangrams Viewed',      this.platformMetrics.tangramsViewed]));
    sections.push(row(['Avg Completion Rate',  `${this.contentIntelligence.avgCompletionRate}%`]));
    sections.push(row(['Avg Rating',           this.contentIntelligence.avgRating.toFixed(1)]));
    sections.push('');

    sections.push('Performance by Content Type');
    sections.push(row(['Type', 'Items', 'Avg Completion (%)', 'Avg Rating', 'Status']));
    this.contentTypeRows.forEach(r => sections.push(row([
      r.type, r.count, `${r.avgCompletion}%`, r.avgRating.toFixed(1), this.contentTypeStatus(r.avgCompletion),
    ])));
    if (this.contentTypeRows.length === 0) { sections.push(row(['No data', '', '', '', ''])); }
    sections.push('');

    sections.push('Feature Utilization');
    sections.push(row(['Feature', 'Total Uses', 'Unique Users', 'Utilization Rate (%)', 'Status']));
    this.featureRows.forEach(r => sections.push(row([
      r.feature, r.uses, r.uniqueUsers, `${r.utilizationPct}%`, this.featureStatus(r.utilizationPct),
    ])));
    if (this.featureRows.length === 0) { sections.push(row(['No data', '', '', '', ''])); }
    sections.push('');

    sections.push('Underused Features (<30% utilization)');
    sections.push(row(['Feature', 'Utilization Rate (%)']));
    const underused = this.underusedFeatures;
    if (underused.length > 0) {
      underused.forEach(r => sections.push(row([r.feature, `${r.utilizationPct}%`])));
    } else {
      sections.push(row(['All features above 30%', '']));
    }

    const csv  = sections.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `admin-analytics-${this.selectedPeriod.replace(/\s/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  goBack(): void { this.router.navigate(['/home']); }

  constructor(
    private authService: AuthService,
    private router: Router,
    private el: ElementRef,
    private analyticsService: AdminAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.currentUserValue;
    // Fire all three in parallel — each section paints as soon as its own
    // request lands, instead of waiting for the slowest one.
    this.loadKpis();
    this.loadContentTypes();
    this.loadFeatures();
  }

  ngAfterViewInit(): void {
    this.attachDragScroll();
  }

  private loadKpis(): void {
    this.loadingKpis = true;
    this.analyticsService.getKpis(this.period).subscribe({
      next: (res) => {
        if (res.success) {
          const d = res.data;
          this.platformMetrics = {
            totalUsers:     d.totalUsers,
            activeUsersPct: d.totalUsers > 0 ? Math.round((d.activeUsers / d.totalUsers) * 100) : 0,
            totalTangrams:  d.totalTangrams,
            tangramsViewed: d.tangramsViewed,
          };
          this.contentIntelligence = {
            avgCompletionRate: d.avgCompletionRate,
            avgRating:         d.avgRating,
          };
        }
        this.loadingKpis = false;
      },
      error: () => { this.loadingKpis = false; },
    });
  }

  private loadContentTypes(): void {
    this.loadingContentTypes = true;
    this.analyticsService.getContentTypes(this.period).subscribe({
      next: (res) => {
        if (res.success) {
          this.contentTypeRows = res.data || [];
          this.contentTypePage = 1;
        }
        this.loadingContentTypes = false;
      },
      error: () => { this.loadingContentTypes = false; },
    });
  }

  private loadFeatures(): void {
    this.loadingFeatures = true;
    this.analyticsService.getFeatures(this.period).subscribe({
      next: (res) => {
        if (res.success) {
          this.featureRows = res.data || [];
          this.featurePage = 1;
        }
        this.loadingFeatures = false;
      },
      error: () => { this.loadingFeatures = false; },
    });
  }

  private attachDragScroll(): void {
    const host = this.el.nativeElement as HTMLElement;

    host.querySelectorAll<HTMLElement>('.table-responsive').forEach(el => {
      if ((el as any).__dragScrollAttached) { return; }
      (el as any).__dragScrollAttached = true;

      let isDown = false;

      el.addEventListener('mousedown', (e: MouseEvent) => {
        isDown = true;
        el.classList.add('is-dragging');
        e.preventDefault();
      });

      const stop = () => { isDown = false; el.classList.remove('is-dragging'); };
      document.addEventListener('mouseup', stop);
      el.addEventListener('mouseleave', stop);

      el.addEventListener('mousemove', (e: MouseEvent) => {
        if (!isDown) { return; }
        e.preventDefault();
        el.scrollLeft -= e.movementX;
      });
    });
  }
}
